// Tense-scoped drill verb selection.
//
// Portuguese "boot" stem-changing -ir verbs only alternate in the present
// indicative (eu) and present subjunctive (all persons). Elsewhere they are
// fully regular for their -ir class. The per-verb `stemChange.tenses` map in
// verbs.json is the single source of truth for which (tense, pronoun) forms
// actually alternate; this module reads that map to build drill pools and to
// generate the in-drill explanation copy.

import { expectedRegularForm, PRONOUN_KEYS, type PronounKey } from './conjugation'

export type DrillTense = 'present' | 'preterite' | 'imperfect' | 'presentSubjunctive' | 'future'
export const DRILL_TENSE_IDS: DrillTense[] = ['present', 'preterite', 'imperfect', 'presentSubjunctive', 'future']

export type StemChange = {
  from: string
  to: string
  // tense -> the pronouns whose form actually shows the alternation.
  tenses: Partial<Record<string, string[]>>
}

export type PoolVerb = {
  infinitive: string
  verbType: string
  conjugations: { [tense: string]: { [pronoun: string]: string | undefined } }
  stemChange?: StemChange
}

export type PoolSettings = {
  tenses: DrillTense[]
  verbTypes: string[]
  pronouns: PronounKey[]
}

export type PoolItem<V> = { verb: V; tense: DrillTense; pronoun: PronounKey }

// "a gente" is its own drill option next to "nós": same meaning ("we"), but it
// takes the 3rd-person singular form (a gente fala), where nós takes -mos
// (nós falamos). It has no column of its own in the tables — it borrows você's.
export type DrillPronoun = PronounKey | 'a gente'
export const GENTE = 'a gente' as const
export function conjugationKey(pronoun: DrillPronoun): PronounKey {
  return pronoun === GENTE ? 'você' : pronoun
}

// A pool item that remembers which drill pronoun asked for it, so "a gente" and
// "você" both pull the você form but are shown (and graded) as different subjects.
export type DrillPoolItem<V> = PoolItem<V> & { via: DrillPronoun }

// buildDrillPool, but over drill pronouns: each ticked pronoun contributes the
// forms for its own conjugation column, tagged with itself.
export function buildDrillPoolFor<V extends PoolVerb>(
  verbs: readonly V[],
  settings: { tenses: DrillTense[]; verbTypes: string[]; pronouns: readonly DrillPronoun[] },
): Array<DrillPoolItem<V>> {
  return settings.pronouns.flatMap((via) =>
    buildDrillPool(verbs, { ...settings, pronouns: [conjugationKey(via)] }).map((item) => ({ ...item, via })),
  )
}

// Does this exact (verb, tense, pronoun) form carry the stem alternation?
export function stemChangeAppliesTo(verb: PoolVerb, tense: string, pronoun: string): boolean {
  return verb.stemChange?.tenses[tense]?.includes(pronoun) ?? false
}

// The regular conjugation class a verb falls back to when it isn't actually
// irregular for the form in question.
function regularClassOf(verb: PoolVerb): string {
  if (verb.infinitive.endsWith('ar')) return 'regular-ar'
  if (verb.infinitive.endsWith('er')) return 'regular-er'
  return 'regular-ir'
}

// True when every modelled form of `verb` in `tense` matches the fully-regular
// form for its class — i.e. the verb is only irregular in other tenses. Most
// "irregular" verbs are like this in the future (ir → irei, ser → serei); only
// fazer/dizer keep a suppletive future stem.
export function isRegularInTense(verb: PoolVerb, tense: string): boolean {
  const forms = verb.conjugations[tense]
  if (!forms) return false
  let checked = 0
  for (let index = 0; index < PRONOUN_KEYS.length; index += 1) {
    const actual = forms[PRONOUN_KEYS[index]]
    if (actual == null) continue
    if (actual !== expectedRegularForm(verb.infinitive, tense, index)) return false
    checked += 1
  }
  return checked > 0
}

// True when the verb form for (tense, pronoun) belongs in a drill for the
// currently-selected verb types.
function isEligible(verb: PoolVerb, tense: DrillTense, pronoun: PronounKey, verbTypes: string[]): boolean {
  if (verb.verbType === 'irregular') {
    // An "irregular" verb whose whole paradigm for this tense is regular
    // (ir/ser/ter/… in the future) is drilled as its regular class here, not
    // as an irregular — so it never shows up under "Irregular" for that tense.
    return isRegularInTense(verb, tense) ? verbTypes.includes(regularClassOf(verb)) : verbTypes.includes('irregular')
  }
  if (verb.verbType === 'stem-changing') {
    return stemChangeAppliesTo(verb, tense, pronoun)
      ? verbTypes.includes('stem-changing')
      : verbTypes.includes(regularClassOf(verb))
  }
  return verbTypes.includes(verb.verbType)
}

// Every (verb, tense, pronoun) combination eligible for the current settings.
// A stem-changing verb only appears under "stem-changing" for the exact forms
// that alternate; for other tenses/pronouns it falls into its regular -Xr pool
// instead — as does an "irregular" verb whose whole paradigm for that tense is
// regular (ir/poder in the future, fazer/estar in the imperfect). If
// "stem-changing" is selected for a tense with no alternations at all, the
// verbs that are genuinely irregular in that tense fill it so the drill is
// never empty.
export function buildDrillPool<V extends PoolVerb>(verbs: readonly V[], settings: PoolSettings): Array<PoolItem<V>> {
  const combos: Array<PoolItem<V>> = []

  for (const tense of settings.tenses) {
    const forTense: Array<PoolItem<V>> = []
    for (const verb of verbs) {
      for (const pronoun of settings.pronouns) {
        if (isEligible(verb, tense, pronoun, settings.verbTypes)) forTense.push({ verb, tense, pronoun })
      }
    }

    if (forTense.length === 0 && settings.verbTypes.includes('stem-changing')) {
      for (const verb of verbs) {
        if (verb.verbType !== 'irregular' || isRegularInTense(verb, tense)) continue
        for (const pronoun of settings.pronouns) forTense.push({ verb, tense, pronoun })
      }
    }

    combos.push(...forTense)
  }

  return combos
}

// Whether the Build-Drill screen should warn that stem changes won't show up
// for the chosen tenses.
export function showStemChangeHint(settings: { verbTypes: string[]; tenses: string[] }): boolean {
  return (
    settings.verbTypes.includes('stem-changing') &&
    !settings.tenses.includes('present') &&
    !settings.tenses.includes('presentSubjunctive')
  )
}

const TENSE_LABEL: Record<string, string> = {
  present: 'the present tense',
  presentSubjunctive: 'the present subjunctive',
}

// In-drill explanation, generated from the same structured map that drives
// selection so it can't drift. e.g. "repetir only changes its stem in the
// present tense (repito) and the present subjunctive — it's fully regular in
// the preterite and other tenses."
export function stemChangeExplanation(verb: PoolVerb): string {
  const change = verb.stemChange
  if (!change) return ''
  const example = verb.conjugations.present?.eu ?? ''
  const parts = Object.keys(change.tenses).map((tense, index) => {
    const label = TENSE_LABEL[tense] ?? tense
    return index === 0 && example ? `${label} (${example})` : label
  })
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : (parts[0] ?? '')
  return `${verb.infinitive} only changes its stem in ${list} — it's fully regular in the preterite and other tenses.`
}

export { expectedRegularForm, PRONOUN_KEYS, type PronounKey }
